// ============================================
// IMPORTANT: Polyfills must be loaded FIRST
// This provides compatibility for Legacy Smart TVs
// (LG WebOS 3.x-5.x, Samsung Tizen 2.x-4.x)
// ============================================
import "./polyfills";

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import toast from "react-hot-toast";
import "./index.css";
import "./style/legacy-tv.css"; // Legacy Smart TV CSS fixes
import "./style/legacy-ui.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import HomeScreen from "./app/page";
import AnaVatanPage from "./app/anavatan/page";
import CityContentPage from "./app/anavatan/city/page";
import CategoriesPage from "./app/categories/page";
import ChildCategory from "./app/categories/parent/child/page";
import LoginPage from "./app/login/page";
import MoviePage from "./app/movie/page";
import ProfilePage from "./app/profile/page";
import SearchPage from "./app/search/page";
import SubscriptionPage from "./app/subscription/page";
import MoviePlayer from "./app/watch/page";
import MoftabakhPage from "./app/moftabakh/page";
import ChildGenre from "./app/genre/parent/child/page";
import SeriesPage from "./app/series/page";
import ProgramPage from "./app/program/page";
import LivePage from "./app/live/page";
import OnlineCinemaPage from "./app/online-cinema/page";
import ProfileFavourtiesPage from "./app/profile/favourites";
import ProfileSubscriptionsPage from "./app/profile/subscriptions";
import ProfileWatchHistoryPage from "./app/profile/watchHistory";
import ArtistPage from "./app/artist/page";
import MovieHome from "./app/movie/home";
import SeriesHome from "./app/series/home";
import MusicHome from "./app/music/home";
import TrailerPlayer from "./app/trailer/page";

// PWA and Preload components
import {
  disablePWAInDevelopment,
  initializePWA,
  isOffline,
  isPWAEnabled,
  onNetworkStatusChange,
} from "./utils/pwaManager";
import PreloadSplash from "./components/common/PreloadSplash";
import ForceUpdateModal from "./components/common/ForceUpdateModal";
import OfflinePage from "./components/common/OfflinePage";

// Version check
import { BUILD_VERSION } from "./config/default";
import { getCMSApiRoute } from "./utils/api.utils";
import axios from "axios";

// Redux Provider - wrap at root level for loading state management
import StateProvider from "./redux/provider";

// Input mode tracking for mouse vs keyboard focus
import { InputModeProvider } from "./context/InputModeContext";

// Sentry Integration
import { initSentry } from "./services/sentry";
import { initGoogleAnalytics, trackScreenView } from "./services/googleAnalytics";
import { AUTH_SESSION_EXPIRED_EVENT } from "./services/authStorage";

// Initialize Sentry early to capture any errors during boot
initSentry();
initGoogleAnalytics();

global.AbortController = window.AbortController;

Number.isInteger =
  Number.isInteger ||
  function (value) {
    return (
      typeof value === "number" &&
      isFinite(value) &&
      Math.floor(value) === value
    );
  };

function resetDocumentViewport() {
  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
    document.documentElement.scrollLeft = 0;
  }
  if (document.body) {
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
  }
}

try {
  if (window.history && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
} catch (error) {
  // Some TV WebViews expose history but reject scrollRestoration writes.
  if (error) resetDocumentViewport();
}

resetDocumentViewport();

var pwaEnabled = isPWAEnabled();

if (pwaEnabled) {
  initializePWA().then(() => {
    console.log('[App] PWA initialized');
  }).catch((error) => {
    console.warn('[App] PWA initialization failed:', error);
  });
} else {
  disablePWAInDevelopment();
}

/**
 * Check if app version is below minimum required
 * @returns {Promise<boolean>} True if update is required
 */
async function checkMinVersion() {
  try {
    const response = await axios.get(getCMSApiRoute("config?device=web_tv"));
    const { data: configData } = response.data;

    const minVersion = parseInt(configData.lastAllowedVer, 10);

    if (isNaN(minVersion)) {
      console.log('[App] No minVersion in config, skipping version check');
      return false;
    }

    console.log('[App] Version check:', { local: BUILD_VERSION, required: minVersion });

    if (BUILD_VERSION < minVersion) {
      console.log('[App] Update required:', BUILD_VERSION, '<', minVersion);
      return true;
    }

    return false;
  } catch (error) {
    console.warn('[App] Version check failed:', error);
    // Don't block app if version check fails
    return false;
  }
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomeScreen />,
  },
  {
    path: "/anavatan",
    element: <AnaVatanPage />,
  },
  {
    path: "/anavatan/city",
    element: <CityContentPage />,
  },
  {
    path: "/category/:slug",
    element: <ChildCategory />,
  },
  {
    path: "/category/view/:slug",
    element: <ChildCategory />,
  },
  {
    path: "/genre/:slug",
    element: <ChildGenre />,
  },
  {
    path: "/categories",
    element: <CategoriesPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/movie",
    element: <MovieHome />,
  },
  {
    path: "/movie/:slug",
    element: <MoviePage />,
  },
  {
    path: "/animation/:slug",
    element: <MoviePage />,
  },
  {
    path: "/music/:slug",
    element: <MoviePage />,
  },
  {
    path: "/music",
    element: <MusicHome />,
  },
  {
    path: "/series",
    element: <SeriesHome />,
  },
  {
    path: "/series/:slug",
    element: <SeriesPage />,
  },
  {
    path: "/program/:slug",
    element: <ProgramPage />,
  },
  {
    path: "/live/:slug",
    element: <LivePage />,
  },
  {
    path: "/sport/:slug",
    element: <LivePage />,
  },
  {
    path: "/profile",
    element: <ProfilePage />,
  },
  {
    path: "/profile/favourites",
    element: <ProfileFavourtiesPage />,
  },
  {
    path: "/profile/subscriptions",
    element: <ProfileSubscriptionsPage />,
  },
  {
    path: "/profile/watch-history",
    element: <ProfileWatchHistoryPage />,
  },
  {
    path: "/search",
    element: <SearchPage />,
  },
  {
    path: "/subscription",
    element: <SubscriptionPage />,
  },
  {
    path: "/trailer/:slug",
    element: <TrailerPlayer />,
  },
  {
    path: "/watch/:slug",
    element: <MoviePlayer />,
  },
  {
    path: "/watch/:slug/:s/:e",
    element: <MoviePlayer />,
  },
  {
    path: "/watch/onlinebakhish/:contentSlug/:isTicketing",
    element: <MoviePlayer />,
  },
  {
    path: "/moftabakh",
    element: <MoftabakhPage />,
  },
  {
    path: "/online-cinema",
    element: <OnlineCinemaPage />,
  },
  {
    path: "/artists/:slug",
    element: <ArtistPage />,
  },
  {
    path: "/offline",
    element: <OfflinePage />,
  },
]);

var lastScreenLocationKey = router.state.location.key;
trackScreenView(router.state.location);
router.subscribe(function (state) {
  if (state.location.key === lastScreenLocationKey) {
    return;
  }
  lastScreenLocationKey = state.location.key;
  trackScreenView(state.location);
});

// App wrapper with offline detection and version check
function App() {
  const [offline, setOffline] = useState(pwaEnabled ? isOffline() : false);
  const [needsUpdate, setNeedsUpdate] = useState(false);

  useEffect(() => {
    function handleSessionExpired() {
      var location = router.state.location;
      if (location.pathname === "/login") return;

      var returnPath =
        location.pathname + (location.search || "") + (location.hash || "");
      toast.error("نشست شما منقضی شده است. لطفا دوباره وارد شوید.");
      router.navigate("/login?ref=" + encodeURIComponent(returnPath), {
        replace: true,
      });
    }

    window.addEventListener(
      AUTH_SESSION_EXPIRED_EVENT,
      handleSessionExpired
    );
    return function () {
      window.removeEventListener(
        AUTH_SESSION_EXPIRED_EVENT,
        handleSessionExpired
      );
    };
  }, []);

  // Check version on mount
  useEffect(() => {
    if (!pwaEnabled) {
      return;
    }

    checkMinVersion().then((outdated) => {
      setNeedsUpdate(outdated);
    });
  }, []);

  // Listen for network status changes
  useEffect(() => {
    if (!pwaEnabled) {
      return;
    }

    onNetworkStatusChange((online) => {
      setOffline(!online);
      console.log('[App] Network status:', online ? 'online' : 'offline');
    });
  }, []);

  // Show force update modal if version is outdated
  if (needsUpdate) {
    return <ForceUpdateModal />;
  }

  var routedContent = offline && !window.location.pathname.includes('/offline') ? (
    <OfflinePage />
  ) : (
    <RouterProvider router={router} />
  );

  return (
    <StateProvider>
      <InputModeProvider>
        {pwaEnabled ? <PreloadSplash>{routedContent}</PreloadSplash> : routedContent}
      </InputModeProvider>
    </StateProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
